/**
 * Smart Shikka Specialist Advisors Preview — Router
 *
 * Routes user queries to appropriate specialist advisors.
 * Returns advisory plans only, NOT executable actions.
 * Source: github.com/msitarzewski/agency-agents (MIT License)
 *
 * no new server route, no real execution, no provider call, no production DB,
 * no RAG/vector mutation, no command/file/browser/desktop/network action
 */
import { AGENCY_AGENTS_REGISTRY } from './agencyAgentsPreviewRegistry.js';
const ROUTE_RULES = Object.freeze([
    {
        keywords: ['code review', 'review code', 'PR review', 'pull request review', 'code quality'],
        advisorIds: ['engineering-code-reviewer'],
        intentLabel: 'code_review',
    },
    {
        keywords: ['small fix', 'safe fix', 'minimal change', 'quick fix', 'one line fix', 'hotfix'],
        advisorIds: ['engineering-minimal-change-engineer'],
        intentLabel: 'minimal_fix',
    },
    {
        keywords: ['samjhao', 'explain', 'onboard', 'feature kaise', 'app samjhao', 'codebase', 'understand code', 'how does this work'],
        advisorIds: ['engineering-codebase-onboarding-engineer', 'sales-coach'],
        intentLabel: 'app_understanding',
    },
    {
        keywords: ['demo', 'presentation', 'show client', 'showcase', 'sales deck', 'pitch'],
        advisorIds: ['sales-engineer', 'engineering-technical-writer'],
        intentLabel: 'demo_presentation',
    },
    {
        keywords: ['audio', 'transcript', 'voice', 'speech', 'STT', 'TTS', 'JSON extract', 'kundali', 'vastu'],
        advisorIds: ['engineering-voice-ai-integration-engineer'],
        intentLabel: 'audio_transcript_json',
    },
    {
        keywords: ['PWA', 'mobile', 'app build', 'mobile panel', 'react native', 'mobile app'],
        advisorIds: ['engineering-mobile-app-builder'],
        intentLabel: 'mobile_pwa',
    },
    {
        keywords: ['security', 'permission', 'appsec', 'vulnerability', 'threat', 'OWASP', 'access control'],
        advisorIds: ['security-appsec-engineer'],
        intentLabel: 'security_permission',
    },
    {
        keywords: ['reality check', 'proof', 'evidence', 'verify', 'honest', 'actually working', 'does it really'],
        advisorIds: ['testing-reality-checker', 'testing-evidence-collector'],
        intentLabel: 'reality_check_proof',
    },
    {
        keywords: ['backend', 'API design', 'system design', 'architecture', 'microservice', 'scalability'],
        advisorIds: ['engineering-backend-architect'],
        intentLabel: 'backend_architecture',
    },
    {
        keywords: ['database', 'query', 'index', 'schema', 'SQL', 'optimization', 'migration'],
        advisorIds: ['engineering-database-optimizer'],
        intentLabel: 'database_optimization',
    },
    {
        keywords: ['documentation', 'docs', 'README', 'API docs', 'technical writing', 'changelog'],
        advisorIds: ['engineering-technical-writer'],
        intentLabel: 'documentation',
    },
    {
        keywords: ['multi agent', 'orchestration', 'agent system', 'coordination', 'delegation'],
        advisorIds: ['engineering-multi-agent-systems-architect'],
        intentLabel: 'multi_agent_systems',
    },
    {
        keywords: ['test workflow', 'CI/CD', 'pipeline', 'test optimization', 'feedback loop'],
        advisorIds: ['testing-workflow-optimizer'],
        intentLabel: 'test_workflow',
    },
    {
        keywords: ['proposal', 'RFP', 'pricing', 'bid', 'sales proposal'],
        advisorIds: ['sales-proposal-strategist'],
        intentLabel: 'sales_proposal',
    },
    {
        keywords: ['sales', 'customer', 'objection', 'value prop', 'deal'],
        advisorIds: ['sales-coach'],
        intentLabel: 'sales_coaching',
    },
]);
function matchQuery(query) {
    const lowerQuery = query.toLowerCase();
    for (const rule of ROUTE_RULES) {
        for (const keyword of rule.keywords) {
            if (lowerQuery.includes(keyword.toLowerCase())) {
                return rule;
            }
        }
    }
    return null;
}
function resolveAdvisors(advisorIds) {
    const resolved = [];
    for (const id of advisorIds) {
        const agent = AGENCY_AGENTS_REGISTRY.find(a => a.id === id);
        if (agent) {
            resolved.push(agent);
        }
    }
    return resolved;
}
export function routeQuery(query) {
    const matchedRule = matchQuery(query);
    if (!matchedRule) {
        return {
            query,
            matchedAdvisors: [],
            plan: {
                queryIntent: 'unmatched',
                selectedAdvisors: [],
                advisorySummary: 'No matching specialist advisor found for this query. Please refine your question.',
                actionType: 'advisory_plan',
                isExecutable: false,
                requiresOwnerApproval: true,
            },
        };
    }
    const advisors = resolveAdvisors(matchedRule.advisorIds);
    const advisorNames = advisors.map(a => a.name);
    const plan = {
        queryIntent: matchedRule.intentLabel,
        selectedAdvisors: advisorNames,
        advisorySummary: `Matched ${advisorNames.length} specialist advisor(s): ${advisorNames.join(', ')}. Advisory plan only — no execution. Owner approval required.`,
        actionType: 'advisory_plan',
        isExecutable: false,
        requiresOwnerApproval: true,
    };
    return {
        query,
        matchedAdvisors: advisors,
        plan,
    };
}
export const ROUTER_POLICY = Object.freeze({
    producesAdvisoryPlansOnly: true,
    noDirectExecution: true,
    noProviderCall: true,
    noFileWrite: true,
    noBrowserAction: true,
    noCommandExecution: true,
    requiresOwnerApproval: true,
    routeRuleCount: ROUTE_RULES.length,
    totalRegisteredAdvisors: AGENCY_AGENTS_REGISTRY.length,
});
//# sourceMappingURL=agencyAgentsPreviewRouter.js.map